import { Printer } from 'lucide-react'
import { type ReactNode } from 'react'

import type { ExchangeDetail } from '@aeris/contracts'
import {
  renderReceiptDocumentHtml,
  renderText,
  type PaperWidth,
} from '@aeris/printing'
import { exchangeToPrint } from '@aeris/sales-print'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Select,
} from '@aeris/ui'

import { printHtmlDocument } from '../lib/label-print.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

/**
 * O comprovante da troca e do vale, na tela e no papel (F126).
 *
 * O mesmo desenho do recibo (`receipt-dialog.tsx`): o documento é DADO montado
 * pelo `@aeris/printing`, a prévia é `renderText` na largura escolhida —
 * caractere por caractere o que a térmica põe na bobina — e imprimir é
 * `window.print()` sobre `#aeris-receipt`. O papel sai do mesmo jeito que o
 * recibo sai hoje, na mesma impressora, com a mesma largura lembrada.
 */

export type ExchangeSlipDialogProps = {
  exchange: ExchangeDetail
  paper: PaperWidth
  onPaperChange: (paper: PaperWidth) => void
  onClose: () => void
}

export const ExchangeSlipDialog = ({
  exchange,
  paper,
  onPaperChange,
  onClose,
}: ExchangeSlipDialogProps): ReactNode => {
  const company = useCompanyHeader()
  const template = useReceiptTemplate()

  // O papel mudou para `@aeris/sales-print` na F307: o celular imprime igual.
  const document = exchangeToPrint({ exchange, company, template })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent title={document.title} description="Confira e imprima.">
        <div className="flex flex-col gap-3">
          <Field label="Papel" className="w-44">
            {(field) => (
              <Select
                {...field}
                value={String(paper)}
                onChange={(event) => {
                  onPaperChange(event.target.value === '48' ? 48 : 32)
                }}
              >
                <option value="32">58 mm</option>
                <option value="48">80 mm</option>
              </Select>
            )}
          </Field>

          {/* `aeris-receipt` é o que a folha de impressão mantém; o resto da
              página some ao imprimir. É o mesmo mecanismo do recibo. */}
          <pre
            id="aeris-receipt"
            aria-label="Comprovante"
            className="overflow-x-auto rounded-(--radius-md) border border-(--border-subtle) bg-(--surface) p-3 font-(--font-mono) text-(length:--text-xs) leading-tight text-(--text-primary)"
          >
            {renderText(document, paper)}
          </pre>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              /*
               * Documento próprio, na largura da bobina e na altura do texto
               * (`renderReceiptDocumentHtml`, F140: negrito, TOTAL grande e o
               * código de barras de verdade). Se o quadro não abrir, cai no
               * jeito antigo, que imprime a página com o resto escondido.
               */
              printHtmlDocument(
                renderReceiptDocumentHtml(document, paper)
              ).catch(() => {
                window.print()
              })
            }}
          >
            Imprimir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
